import React from 'react';
import Combo from '../Combo';
import MultiCombo from '../MultiCombo';
import PanelFiltros from '../PanelFiltros';
import type { Store } from '../../data/stores';
import { colors } from '../../styles/tokens';
import { useEsMovil } from '../../hooks/useEsMovil';
import { useBorrador } from '../../hooks/useBorrador';
import {
  computeMiTiendaOptions,
  firstStoreOfRegional,
  firstStoreOfSupervisor,
  formatLastRefresh,
} from '../../utils/tienda';
import {
  BorradorPeriodo,
  borradorDePeriodo,
  clavePeriodo,
  Periodo,
  periodoDeBorrador,
  validarBorrador,
} from '../../utils/periodo';

/** Lo que aplica el panel de Tienda: la tienda, los colaboradores y el periodo. */
export interface TiendaAplicada {
  storeId: string;
  /** Vacío = toda la tienda. Al cambiar de tienda llega SIEMPRE vacío. */
  vendedorId: string[];
  periodo: Periodo;
}

export interface DrillTiendaProps {
  /** Catálogo completo (SIN filtrar): fuente de los pools + saltos de la cascada. */
  stores: Store[];
  /** Tienda APLICADA — la que ven las consultas y el título de abajo. */
  store: Store;
  /** Periodo APLICADO, compartido con Global. */
  periodo: Periodo;
  /** `lastRefresh` de `GET /dashboard/tienda` (ISO string o `null`). */
  lastRefresh: string | null;
  /**
   * Ítem 11 (2026-07-28): roster de COLABORADORES de la tienda aplicada. Se
   * pasa el COMPLETO (el de la vista sin filtrar): si fuera el de la respuesta
   * ya filtrada, elegir un vendedor dejaría el combo con una sola opción.
   * Omitirlo oculta el campo.
   */
  vendedores?: { id: string; nombre: string }[];
  /** Colaboradores APLICADOS (`external_user_id`); vacío = toda la tienda. */
  vendedorId?: string[];
  /** «Aplicar», Enter en una fecha o un atajo: aplica TODO el panel. */
  onAplicar: (siguiente: TiendaAplicada) => void;
}

interface Borrador {
  storeId: string;
  vendedorId: string[];
  periodo: BorradorPeriodo;
}

const SIN_COLABORADORES: string[] = [];

/** Los colaboradores se comparan como CONJUNTO: el orden de los clics no es un cambio. */
const clave = (storeId: string, vendedorId: string[], p: Periodo) =>
  `${storeId}#${[...vendedorId].sort().join('\u0001')}#${clavePeriodo(p)}`;

/**
 * Panel de la vista Tienda + título de la tienda: el MISMO panel que Global
 * (`PanelFiltros`), con su zona ÁMBITO propia —Regional · Supervisor · Tienda
 * (única) · Colaborador— y la zona PERIODO común.
 *
 * La Tienda es la fuente de verdad del drill: Regional/Supervisor se derivan de
 * ella y elegir uno SALTA a la primera tienda que corresponda (helpers de
 * `utils/tienda.ts`). Todo eso ocurre en el BORRADOR: nada se consulta hasta
 * «Aplicar». Al cambiar de tienda el colaborador se limpia en el mismo paso —
 * los `external_user_id` de una tienda no existen en otra—, y el combo de
 * colaborador se vacía mientras la tienda nueva no esté aplicada, porque su
 * plantilla todavía no se ha pedido.
 */
const DrillTienda: React.FC<DrillTiendaProps> = ({
  stores,
  store,
  periodo,
  lastRefresh,
  vendedores,
  vendedorId = SIN_COLABORADORES,
  onAplicar,
}) => {
  const esMovil = useEsMovil();

  const { borrador, setBorrador, sucio } = useBorrador<Borrador>(
    () => ({ storeId: store.id, vendedorId, periodo: borradorDePeriodo(periodo) }),
    clave(store.id, vendedorId, periodo),
    (b) => clave(b.storeId, b.vendedorId, periodoDeBorrador(b.periodo)),
  );

  const error = validarBorrador(borrador.periodo);
  const storeBorrador = stores.find((s) => s.id === borrador.storeId) ?? store;
  const { miRegionalOpts, miSupervisorOpts, miTiendaOpts } = computeMiTiendaOptions(
    stores,
    storeBorrador,
  );
  const mismaTienda = borrador.storeId === store.id;

  /** Cambiar de tienda limpia el colaborador en el MISMO paso. */
  const elegirTienda = (id: string) =>
    setBorrador((b) => (b.storeId === id ? b : { ...b, storeId: id, vendedorId: SIN_COLABORADORES }));

  const handleRegionalChange = (v: string) => {
    const next = firstStoreOfRegional(stores, v);
    if (next) elegirTienda(next.id);
  };

  const handleSupervisorChange = (v: string) => {
    const next = firstStoreOfSupervisor(stores, storeBorrador.regional, v);
    if (next) elegirTienda(next.id);
  };

  const aplicar = () => {
    if (error) return;
    onAplicar({
      storeId: borrador.storeId,
      vendedorId: mismaTienda ? borrador.vendedorId : SIN_COLABORADORES,
      periodo: periodoDeBorrador(borrador.periodo),
    });
  };

  // Un atajo aplica al instante TODO el panel con ese periodo, y deja el
  // borrador en lo que aplica: si coincide con lo aplicado («Hoy» sobre «Hoy»
  // con una fecha tecleada encima), la pantalla no cambia nada y `useBorrador`
  // no reiniciaría el panel (ver el mismo caso en `Filtros`).
  const atajo = (p: Periodo) => {
    const vendedorIdAtajo = mismaTienda ? borrador.vendedorId : SIN_COLABORADORES;
    setBorrador((b) => ({ ...b, vendedorId: vendedorIdAtajo, periodo: borradorDePeriodo(p) }));
    onAplicar({ storeId: borrador.storeId, vendedorId: vendedorIdAtajo, periodo: p });
  };

  // En escritorio cada campo se separa del anterior con un `border-left` fino;
  // en móvil van uno debajo de otro (mismo criterio que `Filtros`).
  const cellStyle = (index: number, last: boolean): React.CSSProperties =>
    esMovil
      ? { padding: 0, minWidth: 0 }
      : {
          borderLeft: index === 0 ? undefined : `1px solid ${colors.rowBorder}`,
          padding: index === 0 ? '0 14px 0 0' : last ? '0 6px 0 14px' : '0 14px',
          minWidth: 0,
        };

  const enVivo = periodo.modo === 'vivo';

  const ambito = (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: esMovil ? '1fr' : `repeat(${vendedores ? 4 : 3}, minmax(0, 1fr))`,
        gap: esMovil ? 12 : undefined,
      }}
    >
      <div style={cellStyle(0, false)}>
        <Combo
          label="Regional"
          value={storeBorrador.regional}
          options={miRegionalOpts}
          onChange={handleRegionalChange}
          accent
          flat={!esMovil}
          placeholder="Buscar regional…"
        />
      </div>
      <div style={cellStyle(1, false)}>
        <Combo
          label="Supervisor"
          value={storeBorrador.supervisor}
          options={miSupervisorOpts}
          onChange={handleSupervisorChange}
          accent
          flat={!esMovil}
          placeholder="Buscar supervisor…"
        />
      </div>
      {/* `data-testid` estable: las pruebas llegan a este combo por él, no por
          el índice del botón en la pantalla. */}
      <div style={cellStyle(2, !vendedores)} data-testid="drill-tienda-combo">
        <Combo
          label="Tienda"
          value={borrador.storeId}
          options={miTiendaOpts}
          onChange={elegirTienda}
          flat={!esMovil}
          placeholder="Buscar tienda…"
        />
      </div>
      {/* Ítem 11 (2026-07-28): COLABORADOR — acota la vista (embudo, curva,
          bodega, venta y ranking) a uno o varios vendedores. MULTISELECT. */}
      {vendedores && (
        <div style={cellStyle(3, true)} data-testid="drill-colaborador">
          <MultiCombo
            label="Colaborador"
            testid="colaborador"
            allLabel="Toda la tienda"
            value={borrador.vendedorId}
            options={mismaTienda ? vendedores.map((v) => ({ value: v.id, label: v.nombre })) : []}
            onChange={(v) => setBorrador((b) => ({ ...b, vendedorId: v }))}
            flat={!esMovil}
            placeholder={mismaTienda ? 'Buscar colaborador…' : 'Aplica la tienda para ver su plantilla'}
          />
        </div>
      )}
    </div>
  );

  // Hora de la última actualización junto al rótulo PERIODO; en vivo, además,
  // el punto verde que dice que la vista se refresca sola.
  const extraPeriodo = (
    <span
      data-testid="mi-tienda-actualizado"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        fontSize: 12.5,
        color: colors.texto.tertiary,
        fontWeight: 600,
        textTransform: 'none',
        letterSpacing: 0,
        whiteSpace: 'nowrap',
      }}
    >
      {enVivo && (
        <span
          style={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            background: colors.verde,
            animation: 'cmpulse 1.6s infinite',
            flex: '0 0 auto',
          }}
        />
      )}
      actualizado {formatLastRefresh(lastRefresh)}
    </span>
  );

  return (
    <div data-testid="drill-tienda">
      <PanelFiltros
        testid="panel-tienda"
        tituloAmbito="Ámbito a visualizar"
        ambito={ambito}
        baseAmbito="1 1 480px"
        extraPeriodo={extraPeriodo}
        borradorPeriodo={borrador.periodo}
        onBorradorPeriodo={(p) => setBorrador((b) => ({ ...b, periodo: p }))}
        onAtajo={atajo}
        sucio={sucio}
        error={error}
        onAplicar={aplicar}
      />

      {/* ===== Título de la tienda APLICADA (spec §4, ítem 2: debajo del panel) ===== */}
      <div
        data-testid="store-title"
        style={{
          display: 'flex',
          alignItems: 'baseline',
          gap: esMovil ? 8 : 12,
          flexWrap: 'wrap',
          marginTop: esMovil ? 12 : 16,
        }}
      >
        {/* 27 px de titular + el tag de cadena en la misma línea desbordan en
            un teléfono con nombres largos: en móvil el titular baja a 22. */}
        <h2
          style={{
            margin: 0,
            fontSize: esMovil ? 22 : 27,
            fontWeight: 800,
            letterSpacing: '-.02em',
            minWidth: esMovil ? 0 : undefined,
          }}
        >
          {store.tienda}
        </h2>
        <span
          style={{
            fontSize: 11.5,
            fontWeight: 800,
            letterSpacing: '.04em',
            color: colors.brand,
            background: colors.brandSoftBg,
            border: `1px solid ${colors.brandSoftBorder}`,
            borderRadius: 8,
            padding: '4px 10px',
          }}
        >
          {store.cadena}
        </span>
        <span style={{ fontSize: 12.5, color: colors.texto.secondary, fontWeight: 600 }}>
          Supervisor{' '}
          <strong style={{ color: colors.texto.strong, fontWeight: 800 }}>{store.supervisor}</strong>{' '}
          · Regional{' '}
          <strong style={{ color: colors.texto.strong, fontWeight: 800 }}>{store.regional}</strong>
        </span>
      </div>
    </div>
  );
};

export default DrillTienda;
